import React, { useState, useMemo, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { HiCollection } from 'react-icons/hi';

const TrlcrlOneProject = ({ show, onHide, projetoData, projetoId, projetosMap }) => {
    // Extrair tipos únicos e criar mapeamento de cores
    const { tiposUnicos, tipoColorMap } = useMemo(() => {
        // Paleta de cores para tipos dinâmicos
        const COLOR_PALETTE = ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

        if (!projetoData || !projetoData.tipo) {
            return { tiposUnicos: [], tipoColorMap: {} };
        }

        // Ordenar tipos com "REALIZADO" sempre em primeira posição
        const tipos = [...new Set(Object.values(projetoData.tipo))].sort((a, b) => {
            // Se 'a' é "REALIZADO", sempre vem primeiro
            if (a === "REALIZADO") return -1;
            // Se 'b' é "REALIZADO", sempre vem primeiro
            if (b === "REALIZADO") return 1;
            // Caso contrário, ordem alfabética
            return a.localeCompare(b);
        });
        const colorMap = {};
        tipos.forEach((tipo, index) => {
            colorMap[tipo] = COLOR_PALETTE[index % COLOR_PALETTE.length];
        });

        return { tiposUnicos: tipos, tipoColorMap: colorMap };
    }, [projetoData]);

    // Estado para controlar visibilidade dos tipos
    const [tiposVisiveis, setTiposVisiveis] = useState(() => {
        const initialState = {};
        tiposUnicos.forEach(tipo => {
            initialState[tipo] = true;
        });
        return initialState;
    });

    // Estado para controlar posicionamento (deslocado por padrão quando há 2+ tipos)
    const [posicionamentoCentralizado, setPosicionamentoCentralizado] = useState(false);

    // Atualizar estado quando os tipos mudarem
    useEffect(() => {
        if (tiposUnicos.length > 0) {
            setTiposVisiveis(prevState => {
                const newState = { ...prevState };
                tiposUnicos.forEach(tipo => {
                    if (newState[tipo] === undefined) {
                        newState[tipo] = true;
                    }
                });
                return newState;
            });
        }
    }, [tiposUnicos]);

    const MatrizPlotIndividual = () => {
        if (!projetoData || !projetoData.trl || !projetoData.crl || !projetoData.tipo) {
            return (
                <div className="bg-white border border-gray-200 rounded-lg p-12">
                    <div className="text-center text-gray-500">
                        <HiCollection className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>Nenhum dado TRL-CRL encontrado para este projeto</p>
                    </div>
                </div>
            );
        }

        // Obter as chaves dos dados
        const dataKeys = Object.keys(projetoData.trl);
        const trlKeys = Object.keys(projetoData.trl);
        const crlKeys = Object.keys(projetoData.crl);
        const tipoKeys = Object.keys(projetoData.tipo);

        // Verificar consistência dos dados
        if (trlKeys.length !== crlKeys.length || trlKeys.length !== tipoKeys.length) {
            return (
                <div className="bg-white border border-gray-200 rounded-lg p-12">
                    <div className="text-center text-gray-500">
                        <HiCollection className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>Dados TRL-CRL-TIPO inconsistentes para este projeto</p>
                    </div>
                </div>
            );
        }

        // Criar dados de scatter para o projeto individual
        const scatterData = [];
        dataKeys.forEach(key => {
            const trl = projetoData.trl[key];
            const crl = projetoData.crl[key];
            const tipo = projetoData.tipo[key];

            if (trl >= 1 && trl <= 9 && crl >= 1 && crl <= 9 && tipo) {
                scatterData.push({
                    trl: trl,
                    crl: crl,
                    tipo: tipo,
                    color: tipoColorMap[tipo],
                    key: key
                });
            }
        });

        // Filtrar dados baseado na visibilidade dos tipos
        const filteredData = scatterData.filter(point => tiposVisiveis[point.tipo]);

        const visibleScatterData = filteredData.sort((a, b) => {
            const indexA = tiposUnicos.indexOf(a.tipo);
            const indexB = tiposUnicos.indexOf(b.tipo);
            // Ordem decrescente: tipos do final da legenda renderizam primeiro
            return indexB - indexA;
        });

        if (scatterData.length === 0) {
            return (
                <div className="bg-white border border-gray-200 rounded-lg p-12">
                    <div className="text-center text-gray-500">
                        <HiCollection className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p>Nenhum par TRL-CRL válido encontrado</p>
                    </div>
                </div>
            );
        }

        const width = 650;
        const height = 650;
        const margin = { top: 20, right: 50, bottom: 80, left: 80 };
        const plotWidth = width - margin.left - margin.right;
        const cellSize = Math.floor(plotWidth / 9); // Garantir inteiro
        const actualPlotWidth = cellSize * 9;
        const actualPlotHeight = cellSize * 9;

        return (
            <div className="bg-white rounded-lg" style={{ outline: 'none', border: 'none' }}>
                {/* Cabeçalho com título e nome do projeto */}
                <div className="text-center mt-3">
                    <h4 className="font-semibold">
                        Matriz TRL × CRL — {projetoId}
                    </h4>
                    <p className="text-lg font-medium text-gray-600 break-words max-w-2xl mx-auto mb-0">
                        {projetosMap[projetoId] || projetoId}
                    </p>
                </div>

                <div className="px-6 pb-4 flex justify-center items-start gap-8">
                    <svg
                        width={width}
                        height={height}
                        style={{ maxWidth: '100%', height: 'auto', outline: 'none', border: 'none' }}
                        shapeRendering="geometricPrecision"
                        tabIndex="-1"
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        <g transform={`translate(${margin.left}, ${margin.top})`}>
                            {/* Grid 9x9 - desenha 10 linhas (0 a 9) para criar 9 células */}
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                                <g key={`grid-${i}`} style={{ pointerEvents: 'none' }}>
                                    {/* Linhas horizontais */}
                                    <line
                                        x1={0}
                                        y1={i * cellSize}
                                        x2={actualPlotWidth}
                                        y2={i * cellSize}
                                        stroke="#3b82f6"
                                        strokeWidth="1"
                                    />
                                    {/* Linhas verticais */}
                                    <line
                                        x1={i * cellSize}
                                        y1={0}
                                        x2={i * cellSize}
                                        y2={actualPlotHeight}
                                        stroke="#3b82f6"
                                        strokeWidth="1"
                                    />
                                </g>
                            ))}

                            {/* Marcadores X */}
                            {visibleScatterData.map((point, idx) => {
                                // Posição base da célula (centro)
                                const baseCenterX = (point.crl - 1) * cellSize + cellSize / 2;
                                const baseCenterY = (9 - point.trl) * cellSize + cellSize / 2;

                                // Calcular offset baseado na lógica de posicionamento
                                const tipoIndex = tiposUnicos.indexOf(point.tipo);
                                const tiposVisiveis_count = Object.values(tiposVisiveis).filter(v => v).length;

                                let horizontalOffset = 0;
                                let verticalOffset = 0;

                                // Aplicar offset apenas se:
                                // - Há 2+ tipos visíveis E
                                // - Posicionamento não está centralizado
                                if (tiposVisiveis_count >= 2 && !posicionamentoCentralizado) {
                                    horizontalOffset = tipoIndex * 6; // 6px de espaçamento horizontal
                                    verticalOffset = tipoIndex % 2 === 0 ? -3 : 3; // Alternado: par acima, ímpar abaixo
                                }

                                // Posição final
                                const x = baseCenterX + horizontalOffset;
                                const y = baseCenterY + verticalOffset;
                                const size = 18; // Ligeiramente menor para melhor visualização

                                return (
                                    <g key={`marker-${point.key}-${idx}`} style={{ pointerEvents: 'none' }}>
                                        {/* X grande e grosso com cor dinâmica */}
                                        <line
                                            x1={x - size}
                                            y1={y - size}
                                            x2={x + size}
                                            y2={y + size}
                                            stroke={point.color}
                                            strokeWidth="4"
                                            strokeLinecap="round"
                                        />
                                        <line
                                            x1={x - size}
                                            y1={y + size}
                                            x2={x + size}
                                            y2={y - size}
                                            stroke={point.color}
                                            strokeWidth="4"
                                            strokeLinecap="round"
                                        />
                                        {/* Tooltip invisível */}
                                        <title>TRL {point.trl}, CRL {point.crl}: {point.tipo}</title>
                                    </g>
                                );
                            })}

                            {/* Eixo X (CRL) - labels (centralizados nas células) */}
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => (
                                <text
                                    key={`x-label-${i}`}
                                    x={(i - 1) * cellSize + cellSize / 2}
                                    y={actualPlotHeight + 25}
                                    textAnchor="middle"
                                    fontSize="13"
                                    fill="#4b5563"
                                    style={{ pointerEvents: 'none' }}
                                >
                                    {i}
                                </text>
                            ))}

                            {/* Eixo Y (TRL) - labels (centralizados nas células) */}
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => (
                                <text
                                    key={`y-label-${i}`}
                                    x={-15}
                                    y={(9 - i) * cellSize + cellSize / 2 + 5}
                                    textAnchor="end"
                                    fontSize="13"
                                    fill="#4b5563"
                                    style={{ pointerEvents: 'none' }}
                                >
                                    {i}
                                </text>
                            ))}

                            {/* Label do eixo X */}
                            <text
                                x={actualPlotWidth / 2}
                                y={actualPlotHeight + 55}
                                textAnchor="middle"
                                fontSize="14"
                                fontWeight="500"
                                fill="#1f2937"
                            >
                                CRL
                            </text>

                            {/* Label do eixo Y */}
                            <text
                                x={-45}
                                y={actualPlotHeight / 2}
                                textAnchor="middle"
                                fontSize="14"
                                fontWeight="500"
                                fill="#1f2937"
                                transform={`rotate(-90, -45, ${actualPlotHeight / 2})`}
                            >
                                TRL
                            </text>
                        </g>
                    </svg>

                    {/* Legenda Interativa */}
                    <div className="flex flex-col gap-3 mt-3">
                        <div className="flex flex-col gap-1">
                            <h4
                                className="text-sm font-semibold text-gray-700 cursor-pointer hover:text-blue-600 transition-colors duration-200 select-none"
                                onClick={() => setPosicionamentoCentralizado(prev => !prev)}
                                title={posicionamentoCentralizado ? "Clique para deslocar os X" : "Clique para centralizar os X"}
                            >
                                Legenda
                            </h4>
                            {/* <p className="text-xs text-gray-500">
                                {posicionamentoCentralizado ? "Modo: Centralizado" : "Modo: Deslocado"}
                            </p> */}
                        </div>
                        {tiposUnicos.map((tipo) => {
                            const isVisible = tiposVisiveis[tipo];
                            return (
                                <div
                                    key={tipo}
                                    className={`flex items-center gap-3 cursor-pointer p-2 rounded transition-all duration-200 ${isVisible
                                            ? 'bg-gray-50 hover:bg-gray-100'
                                            : 'bg-gray-200 opacity-60 hover:bg-gray-300'
                                        }`}
                                    onClick={() => toggleTipoVisibility(tipo)}
                                >
                                    {/* Miniatura do X */}
                                    <svg width="24" height="24" className="flex-shrink-0">
                                        <g>
                                            <line
                                                x1="4"
                                                y1="4"
                                                x2="20"
                                                y2="20"
                                                stroke={isVisible ? tipoColorMap[tipo] : '#9ca3af'}
                                                strokeWidth="3"
                                                strokeLinecap="round"
                                            />
                                            <line
                                                x1="4"
                                                y1="20"
                                                x2="20"
                                                y2="4"
                                                stroke={isVisible ? tipoColorMap[tipo] : '#9ca3af'}
                                                strokeWidth="3"
                                                strokeLinecap="round"
                                            />
                                        </g>
                                    </svg>

                                    {/* Texto do tipo */}
                                    <span className={`text-sm font-medium select-none ${isVisible ? 'text-gray-700' : 'text-gray-400'
                                        }`}>
                                        {tipo}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    // Função para toggle de visibilidade dos tipos
    const toggleTipoVisibility = (tipo) => {
        setTiposVisiveis(prev => ({
            ...prev,
            [tipo]: !prev[tipo]
        }));
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            {/* <Modal.Header closeButton>
                <Modal.Title>
                    Matriz TRL × CRL - Projeto {projetoId}
                </Modal.Title>
            </Modal.Header> */}

            <Modal.Body>
                <MatrizPlotIndividual />
            </Modal.Body>
            {/* 
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Fechar
                </Button>
            </Modal.Footer> */}
        </Modal>
    );
};

export default TrlcrlOneProject;